import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface LinkedInPost {
  id: string;
  topic: string;
  post_content: string;
  status: string;
  published_at: string | null;
  created_at: string;
}

export const LinkedInPostManager = () => {
  const [topic, setTopic] = useState("");
  const [generatedPost, setGeneratedPost] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [postHistory, setPostHistory] = useState<LinkedInPost[]>([]);
  const [currentPostId, setCurrentPostId] = useState<string | null>(null);

  useEffect(() => {
    fetchPostHistory();
  }, []);

  const fetchPostHistory = async () => {
    const { data, error } = await supabase
      .from('linkedin_posts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error('Error fetching post history:', error);
      return;
    }

    setPostHistory(data || []);
  };

  const formatPostContent = (content: string): string => {
    // Try to parse if it's JSON
    try {
      const parsed = JSON.parse(content);
      if (typeof parsed === 'object' && parsed !== null) {
        // Extract post content from common response formats
        if (parsed.post) return parsed.post;
        if (parsed.output?.post) return parsed.output.post;
        if (parsed.content) return parsed.content;
        if (parsed.text) return parsed.text;
      }
      if (typeof parsed === 'string') return parsed;
    } catch {
      // Not JSON, return as-is
    }
    
    // Clean up escaped newlines
    return content.replace(/\\n/g, '\n').replace(/\\"/g, '"');
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic or idea");
      return;
    }

    setIsGenerating(true);
    setGeneratedPost("");
    setIsEditing(false);
    setCurrentPostId(null);

    try {
      const { data, error } = await supabase.functions.invoke('linkedin-post', {
        body: { action: 'generate', topic: topic.trim() },
      });

      if (error) throw error;

      let postContent = data?.post || "";
      postContent = formatPostContent(postContent);

      setGeneratedPost(postContent);

      // Save to history
      const { data: savedPost, error: saveError } = await supabase
        .from('linkedin_posts')
        .insert({
          topic: topic.trim(),
          post_content: postContent,
          status: 'draft'
        })
        .select()
        .single();

      if (saveError) {
        console.error('Error saving post:', saveError);
      } else {
        setCurrentPostId(savedPost.id);
        fetchPostHistory();
      }

      toast.success("Post generated successfully!");
    } catch (error) {
      console.error("Error generating post:", error);
      toast.error("Failed to generate post. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!generatedPost.trim()) {
      toast.error("No post to publish. Generate a post first.");
      return;
    }

    setIsPublishing(true);

    try {
      const { error } = await supabase.functions.invoke('linkedin-post', {
        body: { action: 'publish', post: generatedPost },
      });

      if (error) throw error;

      // Update status in database
      if (currentPostId) {
        await supabase
          .from('linkedin_posts')
          .update({ 
            status: 'published', 
            published_at: new Date().toISOString(),
            post_content: generatedPost 
          })
          .eq('id', currentPostId);
        
        fetchPostHistory();
      }

      toast.success("Post published to LinkedIn!");
      setTopic("");
      setGeneratedPost("");
      setIsEditing(false);
      setCurrentPostId(null);
    } catch (error) {
      console.error("Error publishing post:", error);
      toast.error("Failed to publish post. Please try again.");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClear = () => {
    setTopic("");
    setGeneratedPost("");
    setIsEditing(false);
    setCurrentPostId(null);
  };

  const handleLoadPost = (post: LinkedInPost) => {
    setTopic(post.topic);
    setGeneratedPost(post.post_content);
    setCurrentPostId(post.id);
    setIsEditing(false);
  };

  const handleDeletePost = async (id: string) => {
    const { error } = await supabase
      .from('linkedin_posts')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error("Failed to delete post");
      return;
    }

    toast.success("Post deleted");
    fetchPostHistory();
    
    if (currentPostId === id) {
      handleClear();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Editor */}
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icon name="linkedin" size={24} />
              LinkedIn Post Generator
            </CardTitle>
            <CardDescription>
              Enter a topic or idea, generate a LinkedIn post with AI, then publish it directly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Topic Input */}
            <div className="space-y-2">
              <Label htmlFor="topic">Topic or Idea</Label>
              <Textarea
                id="topic"
                placeholder="Enter your topic or idea for the LinkedIn post... (e.g., 'The importance of automation in modern marketing')"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            {/* Generate Button */}
            <div className="flex gap-2">
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !topic.trim()}
                className="flex items-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Icon name="loader" size={16} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Icon name="sparkles" size={16} />
                    Generate Post
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={isGenerating || isPublishing}
              >
                Clear
              </Button>
            </div>

            {/* Generated Post Preview */}
            {generatedPost && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold">
                      {isEditing ? "Edit Post" : "Generated Post"}
                    </Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(!isEditing)}
                      className="flex items-center gap-2"
                    >
                      <Icon name={isEditing ? "check" : "edit"} size={14} />
                      {isEditing ? "Done Editing" : "Edit Post"}
                    </Button>
                  </div>

                  {isEditing ? (
                    <Textarea
                      value={generatedPost}
                      onChange={(e) => setGeneratedPost(e.target.value)}
                      className="min-h-[350px] font-sans text-sm"
                    />
                  ) : (
                    <div className="rounded-lg border bg-card p-5 min-h-[200px] shadow-sm">
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        {generatedPost.split('\n').map((paragraph, index) => (
                          <p key={index} className="mb-3 last:mb-0 leading-relaxed">
                            {paragraph || <br />}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handlePublish}
                      disabled={isPublishing || !generatedPost.trim()}
                      className="flex items-center gap-2 bg-[#0077B5] hover:bg-[#006399]"
                    >
                      {isPublishing ? (
                        <>
                          <Icon name="loader" size={16} className="animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <Icon name="linkedin" size={16} />
                          Publish to LinkedIn
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      disabled={isEditing}
                      className="flex items-center gap-2"
                    >
                      <Icon name="edit" size={16} />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleGenerate}
                      disabled={isGenerating || !topic.trim()}
                      className="flex items-center gap-2"
                    >
                      <Icon name="refresh" size={16} />
                      Regenerate
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Post History Sidebar */}
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Icon name="folder" size={18} />
              Post History
            </CardTitle>
            <CardDescription>
              Previously generated posts
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {postHistory.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No posts yet. Generate your first post!
                </div>
              ) : (
                <div className="divide-y">
                  {postHistory.map((post) => (
                    <div
                      key={post.id}
                      className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors ${
                        currentPostId === post.id ? 'bg-muted/70' : ''
                      }`}
                      onClick={() => handleLoadPost(post)}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="font-medium text-sm line-clamp-2">
                          {post.topic}
                        </h4>
                        <Badge 
                          variant={post.status === 'published' ? 'default' : 'secondary'}
                          className="shrink-0 text-xs"
                        >
                          {post.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                        {post.post_content.substring(0, 100)}...
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(post.created_at)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePost(post.id);
                          }}
                        >
                          <Icon name="delete" size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
